import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, Filter, ChevronRight, ChevronLeft, ChevronDown,
  Clock, AlertTriangle, Lock, UserCheck, Check, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { adminApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const ASSIGNABLE_ROLES = new Set(['case_manager', 'compliance_officer', 'triage_manager']);
const ASSIGNER_ROLES = new Set(['company_admin', 'compliance_officer', 'triage_manager', 'super_admin']);
const UNASSIGNED_VALUE = '__unassigned__';

const statusLabels = {
  'RECEBIDA': 'Recebida',
  'EM_TRIAGEM': 'Em Triagem',
  'ADMITIDA': 'Admitida',
  'INADMITIDA': 'Inadmitida',
  'EM_INVESTIGACAO': 'Em Investigação',
  'AGUARDA_INFO': 'Aguarda Info',
  'PENDENTE_APROVACAO': 'Pendente Aprovação',
  'CONCLUIDA': 'Concluída',
  'ARQUIVADA': 'Arquivada'
};

const statusColors = {
  'RECEBIDA': 'bg-slate-100 text-slate-700 border-slate-200',
  'EM_TRIAGEM': 'bg-blue-50 text-blue-700 border-blue-200',
  'ADMITIDA': 'bg-sky-50 text-sky-700 border-sky-200',
  'INADMITIDA': 'bg-red-50 text-red-700 border-red-200',
  'EM_INVESTIGACAO': 'bg-purple-50 text-purple-700 border-purple-200',
  'AGUARDA_INFO': 'bg-amber-50 text-amber-700 border-amber-200',
  'PENDENTE_APROVACAO': 'bg-orange-50 text-orange-700 border-orange-200',
  'CONCLUIDA': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'ARQUIVADA': 'bg-gray-50 text-gray-500 border-gray-200'
};

const severityColors = {
  'BAIXO': 'bg-slate-100 text-slate-600',
  'MEDIO': 'bg-amber-100 text-amber-700',
  'ALTO': 'bg-orange-100 text-orange-700',
  'CRITICO': 'bg-red-100 text-red-700'
};

const ReportsListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    severity: '',
    category: '',
    overdue: searchParams.get('overdue') || ''
  });

  // Bulk assign state (only for assigner roles)
  const canAssign = ASSIGNER_ROLES.has(user?.role);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [tenantUsers, setTenantUsers] = useState([]);
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  const assignableUsers = useMemo(
    () => tenantUsers.filter(u => ASSIGNABLE_ROLES.has(u.role) && u.is_active !== false && !u.cogniseg_managed),
    [tenantUsers],
  );

  const fetchReports = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (filters.status) params.status = filters.status;
      if (filters.severity) params.severity = filters.severity;
      if (filters.category) params.category = filters.category;
      if (filters.overdue) params.overdue = filters.overdue;

      const response = await adminApi.getReports(params);
      setReports(response.data.reports);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total
      });
    } catch (err) {
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Load users list once (assigner roles only) so bulk-assign has candidates.
  useEffect(() => {
    if (!canAssign) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await adminApi.getUsers();
        if (!cancelled) setTenantUsers(res.data || []);
      } catch (_) {
        // Silent — bulk assign just won't offer options.
      }
    })();
    return () => { cancelled = true; };
  }, [canAssign]);

  // Clear selection whenever the visible page changes.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [reports]);

  const toggleReport = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    if (!checked) {
      setSelectedIds(new Set());
    } else {
      const eligible = reports.filter(r => !r.management_locked).map(r => r.id);
      setSelectedIds(new Set(eligible));
    }
  };

  const runBulkAssign = async () => {
    if (!selectedIds.size || !bulkAssignee) return;
    setBulkSaving(true);
    try {
      const payload = {
        report_ids: Array.from(selectedIds),
        assigned_to: bulkAssignee === UNASSIGNED_VALUE ? null : bulkAssignee,
      };
      const res = await adminApi.bulkAssignReports(payload);
      toast.success(res.data.message || 'Denúncias atualizadas');
      setSelectedIds(new Set());
      setBulkAssignee('');
      await fetchReports(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atribuir denúncias');
    } finally {
      setBulkSaving(false);
    }
  };

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      status: searchParams.get('status') || '',
      overdue: searchParams.get('overdue') || ''
    }));
  }, [searchParams]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isOverdue = (report) => {
    const now = new Date();
    if (!report.acknowledgment_sent_at && report.acknowledgment_deadline) {
      return new Date(report.acknowledgment_deadline) < now;
    }
    if (report.feedback_deadline && !['CONCLUIDA', 'ARQUIVADA'].includes(report.status)) {
      return new Date(report.feedback_deadline) < now;
    }
    return false;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Denúncias
          </h1>
          <p className="text-slate-500 mt-1">
            {pagination.total} denúncias no total
          </p>
        </div>
      </div>

      {/* Active overdue filter banner */}
      {filters.overdue && (
        <div
          className={`flex items-center justify-between rounded-lg border px-4 py-3 ${filters.overdue === 'ack' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}
          data-testid="overdue-filter-banner"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${filters.overdue === 'ack' ? 'text-red-600' : 'text-amber-600'}`} />
            <span className={`text-sm font-medium ${filters.overdue === 'ack' ? 'text-red-700' : 'text-amber-700'}`}>
              {filters.overdue === 'ack'
                ? 'A mostrar apenas: Confirmações de receção vencidas (D+7)'
                : 'A mostrar apenas: Feedbacks vencidos (D+90)'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/denuncias')}
            data-testid="clear-overdue-filter"
          >
            Limpar filtro
          </Button>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="w-48">
              <Select
                value={filters.status || 'all'}
                onValueChange={(v) => handleFilterChange('status', v)}
              >
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os estados</SelectItem>
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select
                value={filters.severity || 'all'}
                onValueChange={(v) => handleFilterChange('severity', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Gravidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as gravidades</SelectItem>
                  <SelectItem value="BAIXO">Baixo</SelectItem>
                  <SelectItem value="MEDIO">Médio</SelectItem>
                  <SelectItem value="ALTO">Alto</SelectItem>
                  <SelectItem value="CRITICO">Crítico</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Assign Toolbar (visible to assigner roles when selection is not empty) */}
      {canAssign && selectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3"
          data-testid="bulk-assign-toolbar"
        >
          <span className="text-sm font-medium text-indigo-900 flex items-center gap-1.5">
            <UserCheck className="w-4 h-4" />
            {selectedIds.size} denúncia(s) seleccionada(s)
          </span>
          <Select value={bulkAssignee} onValueChange={setBulkAssignee}>
            <SelectTrigger className="w-72 bg-white" data-testid="bulk-assignee-select">
              <SelectValue placeholder="Atribuir a…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED_VALUE}>Remover atribuição</SelectItem>
              {assignableUsers.length === 0 && (
                <SelectItem value="__none__" disabled>Sem utilizadores atribuíveis</SelectItem>
              )}
              {assignableUsers.map(u => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name} · {u.role.replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={runBulkAssign}
            disabled={!bulkAssignee || bulkSaving}
            className="bg-indigo-700 hover:bg-indigo-800 text-white"
            data-testid="bulk-assign-apply"
          >
            {bulkSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
            Aplicar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelectedIds(new Set())}
            className="text-slate-600"
            data-testid="bulk-assign-clear"
          >
            Limpar
          </Button>
        </div>
      )}

      {/* Reports Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-pulse text-slate-400">A carregar...</div>
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <p>Nenhuma denúncia encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    {canAssign && (
                      <th className="p-4 w-8">
                        <Checkbox
                          checked={selectedIds.size > 0 && selectedIds.size === reports.filter(r => !r.management_locked).length}
                          onCheckedChange={(v) => toggleAll(!!v)}
                          data-testid="bulk-select-all"
                          aria-label="Selecionar todas"
                        />
                      </th>
                    )}
                    <th className="text-left p-4 font-medium text-slate-600">Código</th>
                    <th className="text-left p-4 font-medium text-slate-600">Categoria</th>
                    <th className="text-left p-4 font-medium text-slate-600">Estado</th>
                    <th className="text-left p-4 font-medium text-slate-600">Gravidade</th>
                    <th className="text-left p-4 font-medium text-slate-600">Data</th>
                    <th className="text-left p-4 font-medium text-slate-600">SLA</th>
                    <th className="text-right p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr 
                      key={report.id} 
                      className="border-b hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/admin/denuncias/${report.id}`)}
                      data-testid={`report-row-${report.tracking_code}`}
                    >
                      {canAssign && (
                        <td className="p-4 w-8" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(report.id)}
                            onCheckedChange={() => toggleReport(report.id)}
                            disabled={report.management_locked}
                            data-testid={`bulk-select-${report.tracking_code}`}
                            aria-label={`Selecionar ${report.tracking_code}`}
                          />
                        </td>
                      )}
                      <td className="p-4">
                        <span className="font-mono text-sm font-medium text-slate-900">
                          {report.tracking_code}
                        </span>
                        {report.is_anonymous && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            Anónima
                          </Badge>
                        )}
                        {report.management_locked && (
                          <Badge className="ml-2 text-xs bg-red-100 text-red-700 border-red-200" data-testid={`locked-badge-${report.tracking_code}`}>
                            <Lock className="w-3 h-3 mr-1" /> Bloqueada
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-slate-600">{report.category}</span>
                      </td>
                      <td className="p-4">
                        <Badge className={statusColors[report.status]}>
                          {statusLabels[report.status] || report.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {report.severity ? (
                          <Badge className={severityColors[report.severity]}>
                            {report.severity}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-slate-600">
                          {formatDate(report.created_at)}
                        </span>
                      </td>
                      <td className="p-4">
                        {isOverdue(report) ? (
                          <div className="flex items-center gap-1 text-red-600">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-sm">Vencido</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-emerald-600">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">OK</span>
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Página {pagination.page} de {pagination.pages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => fetchReports(pagination.page - 1)}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.pages}
              onClick={() => fetchReports(pagination.page + 1)}
            >
              Seguinte
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsListPage;
